<script setup lang="ts">
import { computed } from 'vue';
import { CONTENT_TYPE } from '@/utils/const';
import type { ParagraphT } from '@/utils/type';
import {
    isValidCopyrightedImageContent,
    isValidStringContent,
} from '@/tools/contentChecker';
import Image from '@/components/common/Image/Image.vue';

const props = defineProps<{
    // 段落数据
    item: ParagraphT;
}>();

/**
 * 检查段落是否有效
 * 1. item.type 是否在 CONTENT_TYPE 中
 * 2. item.content 的数据格式是否与 item.type 匹配
 */
const isValidParagraph = computed(() => {
    const { item } = props;

    // 检查 type 是否在 CONTENT_TYPE 中
    const validTypes = Object.values(CONTENT_TYPE) as string[];
    if (!validTypes.includes(item.type)) {
        console.warn(`Invalid paragraph type: ${item.type}`);
        return false;
    }

    // 检查 content 的数据格式是否与 type 匹配
    try {
        if (item.type === CONTENT_TYPE.COPYRIGHTED_IMAGE) {
            return isValidCopyrightedImageContent(item.content, item.type);
        } else {
            return isValidStringContent(item.content, item.type);
        }
    } catch (error) {
        console.error(`Error validating paragraph:`, error);
        return false;
    }
});
</script>

<template>
    <div v-if="isValidParagraph" class="paragraph">
        <!-- 文本内容 -->
        <p class="text" v-if="item.type === CONTENT_TYPE.TEXT">
            {{ item.content }}
        </p>

        <!-- 图片内容 -->
        <Image
            v-else-if="item.type === CONTENT_TYPE.IMAGE"
            class="paragraph_image"
            :src="item.content"
            lazy
        />

        <!-- 子标题 -->
        <p class="sub_title" v-if="item.type === CONTENT_TYPE.SUB_TITLE">
            {{ item.content }}
        </p>

        <!-- 版权图片 -->
        <div
            v-if="item.type === CONTENT_TYPE.COPYRIGHTED_IMAGE"
            class="copyrighted_image"
        >
            <Image
                class="paragraph_image"
                :src="item.content.url"
                lazy
                :alt="item.content.copyright"
            />
            <p class="copyright">{{ item.content.copyright }}</p>
        </div>

        <!-- 版权信息 -->
        <p
            class="copyright"
            v-if="item.type === CONTENT_TYPE.ARTICLE_COPYRIGHT"
        >
            {{ item.content }}
        </p>
    </div>
</template>

<style scoped lang="scss">
@use './Paragraph.scss';
</style>
